import React, { useContext, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useConfirm } from 'material-ui-confirm';
import { createColumnHelper } from '@tanstack/react-table';
import { Button, FormControl, IconButton, MenuItem, Select, Stack, Tooltip } from '@mui/material';

import { AppContext } from '../../../../context';
import { translate as t } from 'hooks/useTranslations';
import useGroupManuals from '../_data_sources/useGroupManuals';
import useManageGroupManual from '../_data_sources/useManageGroupManual';
import usePersonas from 'data_sources/usePersonas';
import { ManualPermission, Persona } from 'types/permissions_response';
import Loading from 'views/App/Portal/Loading';
import errorHelper from 'helpers/errorHelper';
import ListSection from 'components/_layouts/ListSection';
import { HelpOutline } from '@mui/icons-material';
import AccessLevelModal from './AccessLevelModal';

const columnHelper = createColumnHelper<ManualPermission>();

export default function Manuals({ roleId }: { roleId: string }) {
  const { currentOrganizationId, isFeatureEnabled } = useContext(AppContext);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const confirm = useConfirm();
  const { id } = useParams();
  const manualsQuery = useGroupManuals(currentOrganizationId || '', id || '');
  const personasQuery = usePersonas({ resourceType: 'Manual' });
  const { addManual, removeManual } = useManageGroupManual();
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const handleHelpOpen = () => setIsHelpOpen(true);
  const handleHelpClose = () => setIsHelpOpen(false);

  const handleExclusionsClick = (manualId: string) => {
    navigate(`/admin/lexid/groups/${id}/manuals/${manualId}/exclusions`);
  };

  const handlePersonaChange = async (manualId: string, personaId: string, priorPersonaId: string) => {
    if (personaId !== priorPersonaId) {
      setIsSaving(true);
      if (personaId?.length > 0) {
        await addManual(
          currentOrganizationId || '',
          roleId,
          manualId,
          personaId,
          () => {
            setIsSaving(false);
            if (!priorPersonaId) {
              queryClient.invalidateQueries('group_manual_permissions');
            }
          },
          (errors) => {
            setIsSaving(false);
            errorHelper.enqueueErrors(errors);
          }
        ).catch((e) => {
          setIsSaving(false);
          console.warn('other error', e);
        });
        if (priorPersonaId) {
          await removeManual(currentOrganizationId || '', roleId, manualId, priorPersonaId);
          queryClient.invalidateQueries('group_manual_permissions');
        }
      } else {
        await confirm({
          description: t('groups.manual_permissions.cancelConfirm'),
        })
          .then(async () => {
            if (priorPersonaId) {
              await removeManual(currentOrganizationId || '', roleId, manualId, priorPersonaId, () => {
                setIsSaving(false);
                queryClient.invalidateQueries('group_manual_permissions');
              });
            }
          })
          .catch(() => {
            // don't do it
            setIsSaving(false);
          });
      }
    }
  };

  const showExclusionsButton = isFeatureEnabled('exclusion-groups');

  const columns = [
    columnHelper.accessor('name', {
      header: () => t('groups.manual_permissions.name'),
      size: 9999,
    }),
    columnHelper.display({
      id: 'actions',
      cell: (info) => (
        <FormControl
          key='persona_picker'
          fullWidth
          sx={{ pt: 0 }}
        >
          <Select
            id='persona_select'
            label={t('groups.manual_permissions.persona')}
            value={info.row.original.persona?.id || ''}
            size='small'
            disabled={isSaving}
            onChange={({ target }) => {
              handlePersonaChange(info.row.original.id, target.value, info.row.original.persona?.id);
            }}
          >
            <MenuItem
              key={`persona_empty`}
              value=''
            >
              --
            </MenuItem>
            {(personasQuery.data?.content ?? []).map((persona: Persona) => (
              <MenuItem
                key={`persona_${persona.id}`}
                value={persona.id}
              >
                {persona.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
      header: () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {t('groups.manual_permissions.persona')}
          <Tooltip title='Policy Manual Access Levels'>
            <IconButton
              size='small'
              onClick={handleHelpOpen}
            >
              <HelpOutline fontSize='small' />
            </IconButton>
          </Tooltip>
        </div>
      ),
      size: 200,
    }),
    ...(showExclusionsButton
      ? [
          columnHelper.display({
            id: 'exclusions',
            cell: (info) => {
              const isExclusionsEnabled = Boolean(info.row.original.persona?.id);
              return (
                <Button
                  variant='outlined'
                  size='small'
                  disabled={!isExclusionsEnabled || isSaving}
                  onClick={() => handleExclusionsClick(info.row.original.id)}
                >
                  {t('groups.manual_permissions.exclusionsButton')}
                </Button>
              );
            },
            header: () => t('groups.manual_permissions.exclusions'),
            size: 150,
          }),
        ]
      : []),
  ];

  const groupManuals = React.useMemo(
    () => (manualsQuery.data && manualsQuery.data.manuals ? manualsQuery.data.manuals : []),
    [manualsQuery.data]
  );

  if (manualsQuery.isError) {
    return errorHelper.renderErrors(manualsQuery);
  }

  return manualsQuery.isLoading || manualsQuery.isFetching ? (
    <Loading />
  ) : (
    <Stack spacing={3}>
      <ListSection
        columns={columns}
        data={groupManuals}
        query={manualsQuery}
      />
      <AccessLevelModal
        isHelpOpen={isHelpOpen}
        handleHelpClose={handleHelpClose}
      />
    </Stack>
  );
}
